import { api } from './api';

// ============ Types ============

export interface BrandContact {
    name: string;
    email: string;
    phone?: string;
    company: string;
    title?: string;
}

export interface ChangeRequest {
    requestId: string;
    priority: 'low' | 'medium' | 'high' | 'critical';
    title: string;
    description: string;
    category: 'content' | 'timing' | 'visual' | 'audio' | 'branding' | 'compliance' | 'other';
    requestedBy: {
        name: string;
        email: string;
        company?: string;
    };
    requestedAt: string;
    estimatedEffort?: string;
    assignedTo?: string;
    status: 'pending' | 'in_progress' | 'completed' | 'rejected' | 'cancelled';
    completedAt?: string;
    attachments: string[];
    internalNotes?: string;
    timestampRef?: string;
    resolution?: string;
}

export interface TimestampFeedback {
    timestamp: string;
    note: string;
    screenshotUrl?: string;
    priority: 'low' | 'medium' | 'high';
    resolved: boolean;
}

export interface BrandFeedback {
    reviewerName: string;
    reviewerEmail: string;
    reviewerCompany?: string;
    decision: 'approved' | 'approved_with_changes' | 'rejected' | 'needs_discussion';
    overallComments?: string;
    timestampFeedback: TimestampFeedback[];
    changeRequestIds: string[];
    reviewDuration?: number;
    submittedAt: string;
}

export interface BrandApproval {
    id: string;
    _id?: string;
    deliverableId?: string;
    taskId?: string;
    projectId: string;
    contractId?: string;
    title: string;
    description?: string;
    versionNumber: number;
    brandContact: BrandContact;
    submittedBy: string;
    submittedAt: string;
    portalLink?: string;
    portalAccessToken?: string;
    portalExpiresAt?: string;
    portalAccessCount: number;
    portalLastAccessedAt?: string;
    previewUrl?: string;
    previewType?: string;
    attachments: string[];
    status: 'pending_brand_review' | 'in_brand_review' | 'approved' | 'approved_with_changes' | 'rejected' | 'cancelled' | 'expired';
    currentRound: number;
    reviewSLA: number;
    reviewDueAt?: string;
    remindersSent: number;
    lastReminderAt?: string;
    brandFeedback: BrandFeedback[];
    changeRequests: ChangeRequest[];
    finalDecision?: 'approved' | 'rejected' | 'cancelled';
    finalizedAt?: string;
    finalizedBy?: {
        name: string;
        email: string;
    };
    averageResponseTime?: number;
    totalRounds: number;
    teamId: string;
    createdAt: string;
    updatedAt: string;
}

export interface BrandApprovalStats {
    pendingBrandReview: number;
    inBrandReview: number;
    approved: number;
    approvedWithChanges: number;
    rejected: number;
    pendingChangeRequests: number;
    avgResponseTime: number;
}

export interface AuditLogEntry {
    id: string;
    brandApprovalId: string;
    action: string;
    actorType: 'creator' | 'brand';
    actor: {
        name: string;
        email: string;
        company?: string;
    };
    details?: Record<string, any>;
    ipAddress?: string;
    userAgent?: string;
    createdAt: string;
}

export interface CreateBrandApprovalDto {
    deliverableId?: string;
    taskId?: string;
    projectId: string;
    contractId?: string;
    title: string;
    description?: string;
    brandContact: BrandContact;
    previewUrl?: string;
    previewType?: string;
    attachments?: string[];
    reviewSLA?: number;
}

export interface UpdateBrandApprovalDto {
    title?: string;
    description?: string;
    brandContact?: BrandContact;
    status?: string;
    previewUrl?: string;
    attachments?: string[];
    reviewSLA?: number;
}

export interface BrandApprovalFilters {
    status?: string;
    projectId?: string;
    brandEmail?: string;
    submittedBy?: string;
}

// ============ API Service ============

export const brandApprovalService = {
    // CRUD Operations
    getApprovals: async (filters?: BrandApprovalFilters): Promise<BrandApproval[]> => {
        const params = new URLSearchParams();
        if (filters) {
            Object.entries(filters).forEach(([key, value]) => {
                if (value) params.append(key, value);
            });
        }
        const response = await api.get(`/brand-approvals?${params.toString()}`);
        return response.data.map((item: any) => ({ ...item, id: item._id || item.id }));
    },

    getApproval: async (id: string): Promise<BrandApproval> => {
        const response = await api.get(`/brand-approvals/${id}`);
        return { ...response.data, id: response.data._id || response.data.id };
    },

    createApproval: async (data: CreateBrandApprovalDto): Promise<BrandApproval> => {
        const response = await api.post('/brand-approvals', data);
        return { ...response.data, id: response.data._id || response.data.id };
    },

    updateApproval: async (id: string, data: UpdateBrandApprovalDto): Promise<BrandApproval> => {
        const response = await api.patch(`/brand-approvals/${id}`, data);
        return { ...response.data, id: response.data._id || response.data.id };
    },

    deleteApproval: async (id: string): Promise<void> => {
        await api.delete(`/brand-approvals/${id}`);
    },

    // Stats
    getStats: async (): Promise<BrandApprovalStats> => {
        const response = await api.get('/brand-approvals/stats');
        return response.data;
    },

    // Version Management
    submitNewVersion: async (id: string, data: { previewUrl?: string; attachments?: string[]; notes?: string }): Promise<BrandApproval> => {
        const response = await api.post(`/brand-approvals/${id}/new-version`, data);
        return { ...response.data, id: response.data._id || response.data.id };
    },

    // Change Request Management
    updateChangeRequest: async (
        approvalId: string,
        requestId: string,
        data: { status?: string; assignedTo?: string; internalNotes?: string; resolution?: string }
    ): Promise<BrandApproval> => {
        const response = await api.patch(`/brand-approvals/${approvalId}/change-requests/${requestId}`, data);
        return { ...response.data, id: response.data._id || response.data.id };
    },

    // Portal Link Management
    regeneratePortalLink: async (id: string): Promise<BrandApproval> => {
        const response = await api.post(`/brand-approvals/${id}/regenerate-link`);
        return { ...response.data, id: response.data._id || response.data.id };
    },

    // Send Reminder
    sendReminder: async (id: string): Promise<BrandApproval> => {
        const response = await api.post(`/brand-approvals/${id}/send-reminder`);
        return { ...response.data, id: response.data._id || response.data.id };
    },

    // Audit Log
    getAuditLog: async (id: string): Promise<AuditLogEntry[]> => {
        const response = await api.get(`/brand-approvals/${id}/audit-log`);
        return response.data.map((item: any) => ({ ...item, id: item._id || item.id }));
    },
};

export default brandApprovalService;
